import {
  BACKTESTING_DISCLAIMER,
  BACKTESTING_DISCLAIMER_ARTICLE,
} from "@shared/features/bots/BotForm/common/constants";
import React, { CSSProperties } from "react";
import styled, { useTheme } from "styled-components";
import { Info } from "lucide-react";
import Text from "common/Typography/Text";
import { WarningIcon } from "common/Icons/Icons";
import IntercomLink from "common/Text/IntercomLink";
import { hexToRGB } from "@shared/utils/misc";
import Flex from "common/Boxes/Flex";
import useMedia from "utils/hooks/useMedia";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { LanguageNameSpaces } from "@shared/types";
import { getNewLookTokens, newLookType } from "common/NewLook/tokens.ts";
import { phoneMaxWidth } from "common/NewLook/layout.ts";

interface DisclaimerProps {
  style?: CSSProperties;
}

const Disclaimer = ({ style }: DisclaimerProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);
  const isSmallScreen = useMedia([`(max-width: 1450px)`], [true], false);
  const { isNewLook } = useTheme();

  if (isNewLook) {
    return (
      <NewLookWrapper style={style}>
        <Info size={14} strokeWidth={1.75} />
        <span>
          {t(BACKTESTING_DISCLAIMER)}
          <NewLookLink articleId={BACKTESTING_DISCLAIMER_ARTICLE}>
            {t("buttons.read_more")}
          </NewLookLink>
        </span>
      </NewLookWrapper>
    );
  }

  return (
    <Wrapper style={style}>
      <StyledWarningIcon />
      <DisclaimerContent $isSmallScreen={isSmallScreen}>
        {t(BACKTESTING_DISCLAIMER)}
        <StyledIntercomLink
          articleId={BACKTESTING_DISCLAIMER_ARTICLE}
          $isSmallScreen={isSmallScreen}
        >
          {t("buttons.read_more")}
        </StyledIntercomLink>
      </DisclaimerContent>
    </Wrapper>
  );
};

export default Disclaimer;

const Wrapper = styled(Flex)`
  background: ${(p) => p.theme.colors.mediumBgColor};
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 12px;
  align-items: flex-start;
  justify-content: flex-start;
  @media (max-width: ${phoneMaxWidth}) {
    padding: 10px;
    margin-bottom: 8px;
  }
`;
const DisclaimerContent = styled(Text)<{ $isSmallScreen: boolean }>`
  width: 100%;
  font-size: ${(p) =>
    p.$isSmallScreen
      ? p.theme.typography.fontSizes.S
      : p.theme.typography.fontSizes.M};
  display: ${(p) => (p.$isSmallScreen ? "inline" : "flex")};
  justify-content: space-between;
`;
const StyledWarningIcon = styled(WarningIcon)`
  margin-right: 8px;
  width: 20px;
  height: 20px;
`;
const StyledIntercomLink = styled(IntercomLink)<{ $isSmallScreen: boolean }>`
  color: ${(p) => p.theme.colors.primaryGrey};
  font-weight: ${(p) => p.theme.typography.fontWeights.SEMI_BOLD};
  text-decoration: underline;
  font-size: ${(p) =>
    p.$isSmallScreen
      ? p.theme.typography.fontSizes.S
      : p.theme.typography.fontSizes.M};
  margin-left: 8px;
  &:hover {
    color: ${(p) => hexToRGB(p.theme.colors.primaryGrey, 0.85)};
    text-decoration: underline;
  }
`;

/* New look: a quiet meta line (no box) — it qualifies the figures, it
   isn't a warning. */
const NewLookWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 6px;
  margin-bottom: 12px;
  ${newLookType.meta};
  color: ${(p) => getNewLookTokens(p.theme).muted};
  svg {
    flex: none;
    margin-top: 1px;
  }
`;
const NewLookLink = styled(IntercomLink)`
  margin-left: 6px;
  font-weight: 500;
  white-space: nowrap;
`;
